import { useState, useRef, useCallback } from 'react';
import { hapticMedium } from '../hooks/useHaptic';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

/**
 * Mobile pull-to-refresh wrapper.
 * Shows a spinner when user pulls down from the top.
 * Only active on mobile (< 768px).
 */
export default function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const THRESHOLD = 60;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (window.innerWidth >= 768) return;
    // Only start pull if at top of scroll
    const scrollParent = (e.currentTarget as HTMLElement).closest('[data-scroll-container]') || e.currentTarget;
    if (scrollParent.scrollTop > 5) return;

    startY.current = e.touches[0].clientY;
    pulling.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pulling.current || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) {
      // Apply resistance curve
      const distance = Math.min(dy * 0.4, 100);
      setPullDistance(distance);
    } else {
      pulling.current = false;
      setPullDistance(0);
    }
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= THRESHOLD && !refreshing) {
      hapticMedium();
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, refreshing, onRefresh]);

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative"
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200 ease-out"
        style={{ height: pullDistance > 0 ? pullDistance : 0 }}
      >
        <div className={`flex items-center gap-2 text-sm text-gray-400 ${
          refreshing ? 'animate-pulse' : ''
        }`}>
          <div className={`w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full transition-transform duration-200 ${
            refreshing ? 'animate-spin' : ''
          }`}
            style={{ transform: refreshing ? undefined : `rotate(${pullDistance * 3.6}deg)` }}
          />
          <span>
            {refreshing ? '刷新中...' : pullDistance >= THRESHOLD ? '松开刷新' : '下拉刷新'}
          </span>
        </div>
      </div>

      {children}
    </div>
  );
}
